import { useMatch } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { FallbackProps } from 'react-error-boundary';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import Spacer from '@antimatterhq/ui/components/spacer';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import ConfigureDataSource from '../configure';
import { useGetDatasourceWithSuspense } from '@/hooks/core/useGetDataSource';
import {
  ConfigDataSourceProvider,
  StepId,
} from '@/contexts/ConfigDataSourceContext';
import { useGetDatasourcePreset } from '@/hooks/content/useGetDatasourcePreset';
import { SuspenseErrorBoundary } from '@/components/errorBoundary/SuspenseErrorBoundary';
import { autoloaderApply } from '@/lib/datasource';

function EditDataSourceSuspense() {
  const match = useMatch('/configure/data-sources/edit/:name/:step');
  const { step = 'source', name = '' } = match?.params || {};
  const { data } = useGetDatasourceWithSuspense({ name });
  const datasource = autoloaderApply(data);
  const { data: preset, isPending } = useGetDatasourcePreset({
    name: datasource.spec.usePreset,
    version: datasource.spec.usePresetVersion,
  });

  if (isPending && datasource.spec.usePreset) {
    return <EditDataSourceSuspenseFallback />;
  }

  return (
    <ConfigDataSourceProvider
      dataSource={datasource}
      currentStep={step as StepId}
      isEdit={true}
      preset={preset}
      data-testid={`edit-datasource-provider-${name}`}
    >
      <div data-testid={`edit-datasource-page-${name}`}>
        <ConfigureDataSource />
      </div>
    </ConfigDataSourceProvider>
  );
}

const EditDataSourceErrorBoundaryFallback: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  const match = useMatch('/configure/data-sources/edit/:name/:step');
  const { name = '' } = match?.params || {};

  const isNotFoundError = isAxiosError(error) && error.response?.status === 404;

  if (isNotFoundError)
    return (
      <GlobalError
        title="Datasource not found"
        description={`Datasource "${name}" could not be found`}
        learnMoreLink="/configure/data-sources"
        learnMoreText="Go to datasources"
        onRetry={async () => resetErrorBoundary()}
        retryText="Retry"
        className="h-[75vh]"
        data-testid={`datasource-not-found-error-${name}`}
      />
    );

  const errorMessage = isAxiosError(error)
    ? (error.response?.statusText ?? error.message)
    : error.message;

  return (
    <GlobalError
      title="Error loading datasource"
      description={errorMessage}
      onRetry={async () => resetErrorBoundary()}
      retryText="Retry"
      className="h-[75vh]"
      data-testid={`datasource-loading-error-${name}`}
    />
  );
};

function EditDataSourceSuspenseFallback() {
  return (
    <div
      className="flex h-full w-full flex-col p-4"
      data-testid="edit-datasource-loading"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <Skeleton
            className="h-14 w-56"
            data-testid="edit-datasource-title-skeleton"
          />
          <Skeleton
            className="h-10 w-full max-w-[850px]"
            data-testid="edit-datasource-steps-skeleton"
          />
        </div>
        <Spacer size="sm" />
        <div
          className="flex max-w-[600px] flex-col gap-2"
          data-testid="edit-datasource-form-skeleton"
        >
          <Skeleton
            className="h-6 w-20"
            data-testid="edit-datasource-form-field-skeleton-1"
          />
          <Skeleton className="h-10 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="edit-datasource-form-field-skeleton-2"
          />
          <Skeleton className="h-28 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="edit-datasource-form-field-skeleton-3"
          />
          <Skeleton className="h-10 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="edit-datasource-form-field-skeleton-4"
          />
          <Skeleton className="h-10 w-20" />

          <Skeleton
            className="h-6 w-20"
            data-testid="edit-datasource-form-field-skeleton-5"
          />
          <Skeleton className="h-10 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="edit-datasource-form-field-skeleton-6"
          />
          <div
            className="flex gap-4"
            data-testid="edit-datasource-buttons-skeleton"
          >
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EditDataSource() {
  return (
    <SuspenseErrorBoundary
      suspenseFallback={<EditDataSourceSuspenseFallback />}
      errorBoundaryFallback={EditDataSourceErrorBoundaryFallback}
      data-testid="edit-datasource-error-boundary"
    >
      <EditDataSourceSuspense />
    </SuspenseErrorBoundary>
  );
}
